/**
 * Persists the "last seen" marker used to compute unread state. localStorage today;
 * a remote per-user store can implement the same interface later. See DESIGN.md.
 */
export interface SeenStore {
  /** ISO timestamp of the newest entry the user has seen, or null if none. */
  getLastSeenAt(): string | null;
  setLastSeenAt(iso: string): void;
  /** Forget the marker so everything is unread again. */
  clear(): void;
}

/** localStorage-backed store, namespaced per app. Degrades to no-op if storage is unavailable. */
export class LocalStorageSeenStore implements SeenStore {
  #key: string;

  constructor(appId: string) {
    this.#key = `clickrelease:seen:${appId}`;
  }

  getLastSeenAt(): string | null {
    try {
      return localStorage.getItem(this.#key);
    } catch {
      return null; // storage blocked (privacy mode, sandboxed iframe)
    }
  }

  setLastSeenAt(iso: string): void {
    try {
      localStorage.setItem(this.#key, iso);
    } catch {
      /* storage unavailable — unread state simply won't persist */
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(this.#key);
    } catch {
      /* ignore */
    }
  }
}

/** In-memory fallback (used in tests / when persistence isn't desired). */
export class MemorySeenStore implements SeenStore {
  #value: string | null = null;
  getLastSeenAt(): string | null {
    return this.#value;
  }
  setLastSeenAt(iso: string): void {
    this.#value = iso;
  }
  clear(): void {
    this.#value = null;
  }
}
